import { Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';

interface UsageProgressProps {
  current: number;
  limit: number;
  label?: string;
  /** Optional explainer shown via an info icon + tooltip after the label. */
  labelInfo?: string;
  /**
   * Compact "k" notation (e.g. 30k) instead of full digits. Only applied when
   * the limit is above 2,000 — small free-tier limits stay as exact numbers.
   * Used in the space-constrained sidebar; Settings keeps full digits.
   */
  abbreviate?: boolean;
  /**
   * `rail` renders only the progress bar (no label, no inline numbers) with the
   * full count in a hover tooltip — for the collapsed icon-rail sidebar where
   * there is no room for text. Defaults to the full label+bar+numbers row.
   */
  variant?: 'full' | 'rail';
  /**
   * The meter's unit (AIT-420). Defaults to 'messages'. Never rendered in the
   * row itself — only in the hover tooltip and the collapsed rail's tooltip,
   * where naming the unit costs nothing.
   */
  unitLabel?: 'messages' | 'actions';
}

/** 30000 -> "30k", 9500 -> "9.5k", 252005 -> "252k", <1000 -> exact. */
function formatCompact(n: number): string {
  if (n < 1000) return n.toLocaleString();
  const k = Math.round((n / 1000) * 10) / 10;
  return `${Number.isInteger(k) ? k : k.toFixed(1)}k`;
}

export function UsageProgress({ current, limit, label, labelInfo, abbreviate, variant = 'full', unitLabel = 'messages' }: UsageProgressProps) {
  // Defensive: a missing usage field must render as 0, never crash the whole
  // page (the usage widget lives in the always-present sidebar).
  const cur = current ?? 0;
  const lim = limit ?? 0;
  // AIT-628: floor, matching the server (AIT-627) — 199/200 is 99%, not 100%.
  const percentage = lim > 0 ? Math.min(100, Math.floor((cur / lim) * 100)) : 0;
  const useCompact = abbreviate === true && lim > 2000;
  const format = (n: number) => (useCompact ? formatCompact(n) : n.toLocaleString());
  const barColor =
    percentage >= 100
      ? 'bg-red-500'
      : percentage >= 80
        ? 'bg-orange-500'
        : percentage >= 50
          ? 'bg-yellow-500'
          : 'bg-primary';

  if (variant === 'rail') {
    // Icon-rail: bar only, full count on hover. No label/numbers (no room).
    return (
      <Tooltip>
        <TooltipTrigger
          render={
            <div className="relative h-1.5 w-full cursor-default overflow-hidden rounded-full bg-primary/20" />
          }
        >
          <div
            className={cn('h-full rounded-full transition-all', barColor)}
            style={{ width: `${percentage}%` }}
          />
        </TooltipTrigger>
        <TooltipContent side="right">
          {label ? `${label}: ` : ''}
          {cur.toLocaleString()} / {lim.toLocaleString()} {unitLabel}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm sm:flex-nowrap">
      {label && (
        <span className="order-1 flex shrink-0 items-center gap-1 text-muted-foreground">
          {label}
          {labelInfo && (
            <Tooltip>
              <TooltipTrigger
                render={
                  <button
                    type="button"
                    aria-label="What counts toward my limit"
                    className="inline-flex cursor-default text-muted-foreground/60 transition-colors hover:text-muted-foreground"
                  />
                }
              >
                <Info className="size-3.5" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">{labelInfo}</TooltipContent>
            </Tooltip>
          )}
        </span>
      )}
      <div className="relative order-3 h-1.5 min-w-10 basis-full overflow-hidden rounded-full bg-primary/20 sm:order-2 sm:flex-1 sm:basis-auto">
        <div
          className={cn('h-full rounded-full transition-all', barColor)}
          style={{ width: `${percentage}%` }}
        />
      </div>
      {(() => {
        // Limit is always shown compact ("2k", "250k") so a big plan's
        // denominator doesn't eat the row; the current count follows the
        // caller's abbreviate flag. Exact figures live in a hover tooltip
        // whenever what we show isn't already the full number.
        // The row is just the two numbers, whatever the unit. The label to
        // its left already says what is being counted, so repeating the word
        // on every meter only crowds the line. The unit still rides along in
        // the hover tooltip, where there is room for it.
        const shown = `${format(cur)} / ${formatCompact(lim)}`;
        const exact = `${cur.toLocaleString()} / ${lim.toLocaleString()}`;
        return shown === exact ? (
          <span className="order-2 ml-auto shrink-0 font-medium sm:order-3 sm:ml-0">{shown}</span>
        ) : (
          <Tooltip>
            <TooltipTrigger
              render={<span className="order-2 ml-auto shrink-0 cursor-default font-medium sm:order-3 sm:ml-0" />}
            >
              {shown}
            </TooltipTrigger>
            <TooltipContent>{exact} {unitLabel}</TooltipContent>
          </Tooltip>
        );
      })()}
    </div>
  );
}
